import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckSquare, FileEdit, StickyNote, Lightbulb, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WebsiteFilter } from '@/components/website/WebsiteFilter';
import { TaskCard } from '@/components/website/TaskCard';
import { NoteCard } from '@/components/website/NoteCard';
import { IdeaTile } from '@/components/website/IdeaTile';
import { ContentCard } from '@/components/website/ContentCard';
import { useWebsiteTasks } from '@/hooks/useWebsiteTasks';
import { useWebsiteNotes } from '@/hooks/useWebsiteNotes';
import { useWebsiteIdeas } from '@/hooks/useWebsiteIdeas';
import { useWebsiteContent } from '@/hooks/useWebsiteContent';
import type { WebsiteName } from '@/types/website';

export default function CRMWebsite() {
  const [websiteFilter, setWebsiteFilter] = useState<WebsiteName | 'all'>('all');
  const filter = websiteFilter === 'all' ? undefined : websiteFilter;

  const { tasks } = useWebsiteTasks(filter);
  const { notes } = useWebsiteNotes(filter);
  const { ideas } = useWebsiteIdeas(filter);
  const { content } = useWebsiteContent(filter);

  const openTasks = tasks.filter(t => t.status !== 'voltooid').slice(0, 5);
  const recentNotes = notes.slice(0, 3);
  const topIdeas = ideas.filter(i => i.status !== 'afgewezen').slice(0, 4);
  const recentContent = content.slice(0, 4);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6" />
            Website: planning
          </h1>
          <p className="text-muted-foreground">Taken, tekstconcepten, notities en ideeën. Wat er op de site gebeurt staat op het <Link to="/crm/website">overzicht</Link>.</p>
        </div>
        <WebsiteFilter value={websiteFilter} onChange={setWebsiteFilter} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CheckSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tasks.filter(t => t.status !== 'voltooid').length}</p>
              <p className="text-sm text-muted-foreground">Open taken</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <FileEdit className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{content.filter(c => c.status === 'live').length}</p>
              <p className="text-sm text-muted-foreground">Live pagina's</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <StickyNote className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{notes.length}</p>
              <p className="text-sm text-muted-foreground">Notities</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Lightbulb className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{ideas.filter(i => i.status === 'nieuw').length}</p>
              <p className="text-sm text-muted-foreground">Nieuwe ideeën</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Open Taken</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/crm/website/taken">Alle taken →</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {openTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Geen open taken</p>
            ) : (
              openTasks.map(task => <TaskCard key={task.id} task={task} />)
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Recente Notities</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/crm/website/notities">Alle notities →</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentNotes.length === 0 ? (
              <p className="text-sm text-muted-foreground">Geen notities</p>
            ) : (
              recentNotes.map(note => <NoteCard key={note.id} note={note} />)
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Top Ideeën</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/crm/website/ideeen">Alle ideeën →</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {topIdeas.length === 0 ? (
                <p className="text-sm text-muted-foreground col-span-2">Geen ideeën</p>
              ) : (
                topIdeas.map(idea => <IdeaTile key={idea.id} idea={idea} />)
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Content</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/crm/website/content">Alle content →</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentContent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Geen content</p>
            ) : (
              recentContent.map(c => <ContentCard key={c.id} content={c} />)
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}